import { StackActions, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ActivityIndicator, FlatList, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from "react-native-vector-icons/FontAwesome5";
import { useSelector, useStore } from 'react-redux';
import { AvatarWithStatus } from '../components/avatar-with-status';
import { HeaderComponent } from '../components/header';
import { ContactCard } from "../components/main/ContactCard";
import { TelegramModal } from '../components/main/TelegramModal';
import { ViewSlide } from '../components/view-slide';
import { useAuthService } from "../services/auth-service";
import { numbersService } from '../services/numbers.service';
import { useRouter } from '../services/router-service';
import { socketService } from '../services/socketio';
import { isApp } from '../shared';
import { APP } from '../shared/constants';
import { globalStyles } from '../shared/styles';
import { RootState } from '../store';
import { collapseAll } from '../store/agenda/agenda.actions';
import { setIsFirstLoaded, setIsLoading } from "../store/ui/ui.actions";

export const MainScreen = () => {

  const navigation = useNavigation()
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const { t } = useTranslation()

  const authState = useSelector((state: RootState) => state.auth)
  const uiState = useSelector((state: RootState) => state.ui)
  const agendaState = useSelector((state: RootState) => state.agenda)
  const appState = useSelector((state: RootState) => state.ui.appState)

  const [previousAppState,setPreviousAppState] = useState<string>(appState)

  const authService = useAuthService()

  const [showConnectionManager, setShowConnectionManager] = useState<boolean>(false)

  useFocusEffect(useCallback(() => {
    (async () => {
      if (appState == "active" && previousAppState != "active" ) {
        await new Promise( r => setTimeout( r, 100 ))
        console.log("useFocusEffect waitSocketReady...")
        await socketService.waitSocketReady()
        console.log("useFocusEffect getContacts...")
        numbersService.getContacts({ forceBackend: false })
      }
      setPreviousAppState( appState )
    })()
  }, [appState])
  )

  useFocusEffect(useCallback(() => {
    try {
      store.dispatch(setIsFirstLoaded(false))
      store.dispatch(collapseAll())
      numbersService.getContacts()
        .finally(() => {
          store.dispatch(setIsLoading(false))
          setTimeout(() => {
            store.dispatch(setIsFirstLoaded(true))
          }, 100)
        })

    } catch (e) {
      console.error(e)
    }
  }, [])
  )

  useFocusEffect(useCallback(() => {
    router.functionalGuard({from:"Main"})
  }, [authService.isWALoggedIn,authService.isLoggedIn,authService.isValidSubscription])
  )

  useFocusEffect(
    useCallback(() => {
      (async () => {
        if ((route.params as any)?.withoutAnimation) navigation.setOptions({
          animationEnabled: false,
          cardStyleInterpolator: undefined, 
        });
      })()
    }, [])
  );

  const toggleShowConnectionManager = useCallback(() => {
    setShowConnectionManager(!showConnectionManager)
  }, [showConnectionManager, setShowConnectionManager])

  const toggleConnectionStatus = useCallback(async () => {
    //store.dispatch(setIsLoading(true))
    await authService.toggleOnlineStatus()
    store.dispatch(setIsLoading(false))
  }, [])

  const addContact = useCallback(() => {
    navigation.dispatch(StackActions.push('AddNumber' as never))
  }, [])

  return (
    <View style={globalStyles.screenBase} >
      <HeaderComponent canGoBack={false} />
      <FlatList
        showsVerticalScrollIndicator={uiState.isFirstLoaded}
        ListHeaderComponent={
          <>
            {(!authState?.userInfo || !uiState.isFirstLoaded) &&
              <View style={{ width: "100%", height: "100%", justifyContent: "center", alignItems: "center" }}>
                <ActivityIndicator color={"white"} size={30} />
              </View>
            }

            {authState?.userInfo && <View style={[globalStyles.mainCardBackground, styles.mainCard, { opacity: uiState.isFirstLoaded ? 1 : 0 }]}>

              <View style={{ flexDirection: 'row', alignItems: 'center', width: "100%", paddingVertical: 14, paddingHorizontal: 12, }}>
                <AvatarWithStatus profilePic={authState?.userInfo?.profilePic} isOnline={authState.onlineStatus == 'online'} />
                <View style={styles.userInfoSection}>
                  <Text style={styles.userInfoName}>{authState?.userInfo?.name}</Text>
                  <Text style={styles.userInfoSim}>+{authState?.userInfo?.sim}</Text>
                </View>
              </View>
              <View style={{ width: "100%", backgroundColor: "white" }}>

                <TouchableOpacity style={{ width: "100%" }} onPress={() => toggleShowConnectionManager()}>
                  <View style={{ ...styles.showConnectionManagerButton, backgroundColor: isApp(APP.CHATWATCH) ? globalStyles.contactCard.backgroundColor : 'rgb(1 105 218)'}} >
                    <View style={{ transform: [{ rotate: showConnectionManager ? '180deg' : '0deg' }] }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 21.5625C13.8913 21.5625 15.7401 21.0017 17.3126 19.9509C18.8852 18.9002 20.1108 17.4067 20.8346 15.6594C21.5584 13.9121 21.7477 11.9894 21.3788 10.1344C21.0098 8.27951 20.0991 6.57563 18.7617 5.23829C17.4244 3.90095 15.7205 2.99021 13.8656 2.62124C12.0106 2.25227 10.0879 2.44164 8.34059 3.1654C6.59328 3.88917 5.09982 5.11482 4.04908 6.68736C2.99834 8.25991 2.4375 10.1087 2.4375 12C2.44048 14.5352 3.44891 16.9657 5.24159 18.7584C7.03426 20.5511 9.46478 21.5595 12 21.5625ZM12 3.5625C13.6688 3.5625 15.3001 4.05735 16.6876 4.98448C18.0752 5.9116 19.1566 7.22936 19.7952 8.77111C20.4339 10.3129 20.6009 12.0094 20.2754 13.6461C19.9498 15.2828 19.1462 16.7862 17.9662 17.9662C16.7862 19.1462 15.2828 19.9498 13.6461 20.2754C12.0094 20.6009 10.3129 20.4338 8.77111 19.7952C7.22936 19.1566 5.9116 18.0752 4.98448 16.6876C4.05736 15.3001 3.5625 13.6688 3.5625 12C3.56499 9.763 4.45473 7.61833 6.03653 6.03653C7.61833 4.45473 9.763 3.56498 12 3.5625ZM8.6025 13.1475C8.54724 13.096 8.50291 13.0339 8.47217 12.9649C8.44142 12.8959 8.42489 12.8214 8.42356 12.7459C8.42223 12.6704 8.43612 12.5953 8.46441 12.5253C8.4927 12.4553 8.53481 12.3916 8.58823 12.3382C8.64164 12.2848 8.70527 12.2427 8.77531 12.2144C8.84535 12.1861 8.92037 12.1722 8.9959 12.1736C9.07142 12.1749 9.14591 12.1914 9.21491 12.2222C9.28391 12.2529 9.34601 12.2972 9.3975 12.3525L11.4375 14.3916L11.4375 8.25C11.4375 8.10082 11.4968 7.95774 11.6023 7.85225C11.7077 7.74676 11.8508 7.6875 12 7.6875C12.1492 7.6875 12.2923 7.74676 12.3978 7.85225C12.5032 7.95774 12.5625 8.10082 12.5625 8.25L12.5625 14.3916L14.6025 12.3525C14.7091 12.2531 14.8502 12.199 14.9959 12.2016C15.1416 12.2042 15.2807 12.2632 15.3837 12.3663C15.4868 12.4693 15.5458 12.6084 15.5484 12.7541C15.551 12.8998 15.4969 13.0409 15.3975 13.1475L12.3975 16.1475C12.292 16.2528 12.1491 16.312 12 16.312C11.8509 16.312 11.708 16.2528 11.6025 16.1475L8.6025 13.1475Z" fill={ isApp(APP.CHATWATCH) ? globalStyles.mainCardBackground.backgroundColor : 'white'} />
                      </svg>
                    </View>
                    <Text style={{ marginLeft: 10, fontFamily: globalStyles.fontFamilyBold.fontFamily, textTransform: 'uppercase', color: isApp(APP.CHATWATCH) ? globalStyles.mainCardBackground.backgroundColor : 'white' }} numberOfLines={1}>{ t('main.connectionManager') }</Text>
                  </View>
                </TouchableOpacity>

                <ViewSlide show={showConnectionManager && authState.onlineStatus == 'online'}>
                  <View style={{ paddingBottom: 10, paddingHorizontal: 14 }}>
                  <Trans i18nKey="main.contactTrackingActive">
                    <Text style={[styles.connectionManagerText, { marginBottom: 10, color: '#1F024A' }]}>Contact tracking is <Text style={{ color: globalStyles.mainCardActiveColor.color, fontWeight: 'bold' }}>active</Text>.<br/> You can deactivate tracking to
                      appear offline. Later, you can activate it with one click. There is no need scan the QR code again.</Text>
                      </Trans>
                    <TouchableOpacity onPress={() => toggleConnectionStatus()} disabled={uiState.togglingOnlineStatus}>
                      <View style={[styles.connectionManagerButton, { backgroundColor: 'black', opacity: uiState.togglingOnlineStatus ? 0.5 : 1 }]} >
                        { !uiState.togglingOnlineStatus && <Text style={[styles.connectionManagerText, { color: 'white', fontWeight: '600' }]}>{ t("main.deactivateTracking") }</Text> }
                        { uiState.togglingOnlineStatus && <ActivityIndicator size={16} color={"white"} style={{ marginLeft: 8 }} /> }
                      </View>
                    </TouchableOpacity>
                  </View>
                </ViewSlide>

                <ViewSlide show={showConnectionManager && authState.onlineStatus == 'offline'}>
                  <View style={{ marginBottom: 10, paddingHorizontal: 14 }}>
                  <Trans i18nKey="main.contactTrackingInactive">
                    <Text style={[styles.connectionManagerText, { marginBottom: 10 }]}>Contact tracking is <Text style={{ color: '#d9534f', fontWeight: 'bold' }}>inactive</Text>.</Text>
                    </Trans>
                    <TouchableOpacity onPress={() => toggleConnectionStatus()} disabled={uiState.togglingOnlineStatus}>
                      <View style={[styles.connectionManagerButton, { backgroundColor: 'black', opacity: uiState.togglingOnlineStatus ? 0.5 : 1 }]} >
                        { !uiState.togglingOnlineStatus && <Text style={[styles.connectionManagerText, { color: 'white', fontWeight: '600' }]}>{t('main.activateTracking')}</Text> }
                        { uiState.togglingOnlineStatus && <ActivityIndicator size={16} color={"white"} style={{ marginLeft: 8 }} /> }
                      </View>
                    </TouchableOpacity>
                  </View>
                </ViewSlide>
              </View>

            </View>
            }
            {!!agendaState.subscribed.length && uiState.isFirstLoaded &&
              <View style={{ width: "100%" }}>
                <Text style={[globalStyles.title, { fontSize: 15, marginVertical: 16 }]}>{t('main.trackedContacts')}</Text>
              </View>}
          </>
        }
        style={{ flexGrow: 1, opacity: uiState.isFirstLoaded ? 1 : 0, width: "100%", maxWidth: 600 }}
        contentContainerStyle={[styles.main, { alignItems: "stretch", width: "100%", maxWidth: 600 }]}
        data={agendaState.subscribed}
        renderItem={({ item: contact }) => uiState.isFirstLoaded ? <ContactCard contact={contact} /> : null}
        keyExtractor={item => item.phone}
      />


      {agendaState.subscribed.length < authState.subscription?.maxNumbers ?
        <View style={{ paddingHorizontal: 20, paddingBottom: 17, maxWidth: 600, width: "100%" }}>
          <TouchableOpacity style={globalStyles.primaryButton} onPress={() => addContact()} disabled={uiState.isLoading}>
            <Icon size={17} color={'white'} name={"plus"} light />
            <Text style={[globalStyles.primaryButtonText, { color: 'white', fontWeight: 'bold', marginLeft: 10, fontSize: 17, textTransform: "uppercase" }]}>{t('main.addContact')}</Text>
          </TouchableOpacity>
        </View>
        : null}
      <TelegramModal />
    </View>
  )

}


const styles = StyleSheet.create({
  containerCenter: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  main: {
    flexGrow: 1,
    alignItems: 'center',
    padding: 24,
    paddingTop: 0,
    maxWidth: 600,
    alignSelf: 'center'
  },
  mainCard: {
    flexDirection: 'column',
    height: 'auto',
    borderRadius: 13,
    alignItems: 'center',
    overflow: "hidden"
  },
  userInfoSection: {
    flexGrow: 1,
    flexDirection: 'column',
    marginLeft: 14
  },
  userInfoName: {
    ...globalStyles.fontFamily,
    fontSize: 17,
    fontWeight: '600',
    color: 'white'
  },
  userInfoSim: {
    ...globalStyles.fontFamily,
    fontSize: 15,
    fontWeight: "500",
    color: 'white'
  },
  showConnectionManagerButton: {
    ...globalStyles.fontFamily,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: "100%",
    height: 46,
    alignSelf: "center"
  },
  connectionManagerButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(38,50,56,0.3)',
    borderRadius: 20,
    width: '100%',
    height: 40,
    paddingTop: 0
  },
  connectionManagerText: {
    fontSize: 14,
    ...globalStyles.fontFamily,
  },
  footer: {
    height: 25
  },
  body: {
    ...globalStyles.fontFamily,
    fontSize: 19,
  },
  listItem: {
    marginTop: 12,
    textAlign: 'center'
  },
  contactCard: {
    flexDirection: 'column',
    backgroundColor: '#f5f5f5',
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 13,
    alignItems: 'center',
    marginVertical: 9,
    borderWidth: 0,
    overflow: "hidden"
  },
  contactInfoSection: {
    flexGrow: 1,
    height: '100%',
    flexDirection: 'column',
    marginLeft: 14,
    paddingVertical: 0,
    justifyContent: 'center'
  },
  contactInfoName: {
    ...globalStyles.fontFamily,
    fontSize: 17,
    fontWeight: 'bold',
  },
  contactInfoNumber: {
    ...globalStyles.fontFamily,
    fontSize: 15,
    opacity: 0.8
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: 'lightgray',
    marginTop: 10,
    marginBottom: 16
  },
  iconText: {
    ...globalStyles.fontFamily,
    fontSize: 10,
    textAlign: 'center',
    lineHeight: 13,
    marginTop: 9,
    color: "#1F024A"
  },
});